import React, { type ForwardedRef, forwardRef } from 'react';

import classnames from 'classnames';

import { useIsOverflown } from '../../hooks/useIsOverflown';
import { useMergeRefs } from '../../hooks/useMergeRefs';
import { Tooltip } from '../Tooltip';

import { type TypographyComponentWithTypeProps } from './Typography.types';

import styles from './Typography.module.scss';

const DEFAULT_COMPONENT = 'span';

/**
 * Base typography component.
 * It should be used only by other typography components.
 * @internal
 */
const TypographyBase = forwardRef(
    (
        {
            type,
            color,
            disabled,
            className,
            as,
            compact,
            link,
            truncate,
            tooltip,
            children,
            ...rest
        }: TypographyComponentWithTypeProps,
        ref?: ForwardedRef<HTMLElement>
    ) => {
        const [overFlowRef, isOverflown] = useIsOverflown(!truncate);
        const mergedRef = useMergeRefs([ref, overFlowRef]);
        const Component = as || DEFAULT_COMPONENT;

        const textNode = (
            <Component
                ref={mergedRef}
                {...rest}
                className={classnames(
                    className,
                    styles.text,
                    styles[`type-${type || 'text'}`],
                    styles[`color-${color || 'inherit'}`],
                    {
                        [styles.disabled]: disabled,
                        [styles.compact]: compact,
                        [styles.link]: link,
                        [styles.truncate]: truncate
                    }
                )}
            >
                {children}
            </Component>
        );
        if (!truncate || !isOverflown) {
            return textNode;
        }
        return (
            <Tooltip textAlignment="center" title={tooltip ?? children}>
                {textNode}
            </Tooltip>
        );
    }
);

TypographyBase.displayName = 'TypographyBase';

export default TypographyBase;
